import pl from 'pluralize';
import clone from 'rfdc';

import RepositoryBaseService from './RepositoryBaseService';
import { GetWebhooksArgs } from './WebhookMigrationService';
import { BuildEntryOptions } from '~/models';
import { WebhookSubscription } from '~/models/Contensis';
import ContensisRepository from '~/repositories/ContensisRepository';
import { compareFields } from '~/util/EntryHelper';
import { Logger } from '~/util/logger';

class WebhookSubscriptionService extends RepositoryBaseService {
  subscriptions?: WebhookSubscription[] = [];
  migrate: any[] = [];
  error: AppError | null = null;

  get totalCount() {
    return this.subscriptions?.length || 0;
  }

  constructor(contensis: ContensisRepository, source: ContensisRepository) {
    super(contensis, source);
  }

  GetSubscriptions = async ({
    name,
    ids,
    enabled,
  }: GetWebhooksArgs = {}): Promise<
    [ApiErrorResponse | null, WebhookSubscription[] | undefined]
  > => {
    Logger.info(`Getting Webhook subscriptions from '${this.contensis.alias}'`);
    const [error, subscriptions] = await this.contensis.repo.GetWebhooks();

    if (error) {
      this.error = error;
    }
    if (subscriptions?.length) {
      if (name) {
        this.subscriptions = subscriptions.filter((s) =>
          s.name.trim().toLowerCase().includes(name.toLowerCase())
        );
      } else if (ids && ids.length) {
        this.subscriptions = subscriptions.filter((s) => ids.includes(s.id));
      } else {
        this.subscriptions = subscriptions;
      }

      if (enabled !== undefined) {
        this.subscriptions = this.subscriptions.filter(
          (s) => s.enabled === enabled
        );
      }
    }
    return [this.error, this.subscriptions];
  };

  GetSubscription = async (
    subscriptionId: string
  ): Promise<[ApiErrorResponse | null, WebhookSubscription[] | undefined]> => {
    Logger.info(`Getting Webhook subscriptions from '${this.contensis.alias}'`);
    [this.error, this.subscriptions] =
      await this.contensis.repo.GetWebhooks(subscriptionId);
    return [this.error, this.subscriptions];
  };

  BuildMigrateWebhooks = async ({ onError }: BuildEntryOptions) => {
    const { contensis, source } = this;

    const webhooks = source.webhooks.subscriptions || [];
    if (webhooks.length)
      Logger.info(
        `Building ${pl('webhook subscription', webhooks.length, true)}`,
        true
      );

    for (const webhook of webhooks) {
      // Webhooks don't seem to respect the id on create, so we need to check both id and name
      const existingById = contensis.webhooks.subscriptions?.find(
        (e) => e.id === contensis.transformGuid(webhook.id)
      );
      const existingByName = contensis.webhooks.subscriptions?.find(
        (e) => e.name === webhook.name
      );
      const existing = existingById || existingByName;

      // Build migrate webhook
      const migrateWebhook = {
        existing,
        diff: '',
        id: '',
        originalId: webhook.id,
        source: webhook,
        webhook: clone()(webhook),
        status: '',
      };
      // Webhooks don't seem to respect the id on create, so we need to hold the existing id if we have one
      migrateWebhook.id = existing?.id || contensis.transformGuid(webhook.id);
      migrateWebhook.webhook.id = migrateWebhook.id;

      if (existing) {
        const { changed, diff } = compareFields(
          migrateWebhook.webhook,
          existing,
          source,
          contensis,
          ['id', 'version']
        );

        if (changed) {
          migrateWebhook.diff = diff || '';
          migrateWebhook.status = 'update';
        } else {
          migrateWebhook.status = 'no change';
        }
      } else {
        migrateWebhook.status = 'create';
      }
      this.migrate.push(migrateWebhook);
      // if (migrateWebhook.status === 'error') onError(migrateWebhook);
    }
  };

  CreateSubscription = async (
    payload: WebhookSubscription
  ): Promise<[ApiErrorResponse | null, WebhookSubscription | undefined]> => {
    Logger.info(`Creating Webhook subscription '${name}'`);
    const [error, subscription] =
      await this.contensis.repo.CreateWebhook(payload);

    if (error) {
      this.error = error;
    }

    if (subscription) this.subscriptions?.push(subscription);
    return [error, subscription];
  };

  UpdateSubscription = async (
    id: string,
    payload: Partial<WebhookSubscription>
  ): Promise<[ApiErrorResponse | null, WebhookSubscription | undefined]> => {
    Logger.info(`Updating Webhook subscription '${id}' name: ${payload.name}`);

    await this.contensis.repo.GetWebhooks(id);
    if (!this.error && this.subscriptions?.length) {
      const existingSubscription = this.subscriptions.find((s) => s.id === id);

      const [error, updatedSub] = await this.contensis.repo.UpdateWebhook({
        ...existingSubscription,
        ...payload,
        id,
      });
      if (error) {
        this.error = error;
      }
      if (updatedSub) {
        this.subscriptions = this.subscriptions.filter((k) => k.id !== id);
        this.subscriptions.push(updatedSub);
      }
      return [error, updatedSub];
    } else {
      return [this.error, undefined];
    }
  };

  DeleteSubscription = async (
    id: string
  ): Promise<[ApiErrorResponse | null, undefined]> => {
    Logger.info(`Removing Webhook subscription '${id}'`);
    [this.error] = await this.contensis.repo.DeleteWebhook(id);
    return [this.error, undefined];
  };
}

export default WebhookSubscriptionService;
